/**
 * Session state.
 *
 * PIN verification happens against the locally cached hash, so signing in works with no
 * connectivity — which it has to, because the counter does not stop when the internet does.
 *
 * The honest limit of this: a registered device holds every user's PIN hash. That is the
 * deliberate trade for offline login. The security boundary is possession of a registered
 * device plus the owner's ability to revoke it, backed by the bitácora — not the hash.
 */

import { useSyncExternalStore } from 'react';
import { can, verifyPin, type PayloadInputOf, type Permission, type Role } from '@abastece/shared';
import { db, delMeta, getMeta, setMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { hayQuePedirPin, marcarUso, MINUTOS_SIN_USO, ultimoUso } from './bloqueo.js';
import { emitir } from './emitir.js';
import { anotarAcierto, anotarFallo, esperaDe } from './intentos.js';

export interface Sesion {
  usuarioId: string;
  nombre: string;
  rol: Role;
}

let sesion: Sesion | null = null;
/**
 * The screen is locked: `sesion` is still whoever was signed in, and everything they left half
 * done is still on screen behind their PIN pad. Kept in localStorage too, so that reloading is
 * not a way past it.
 */
let bloqueada = false;
const CLAVE_BLOQUEADA = 'abastece.bloqueada';
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useSesion(): Sesion | null {
  return useSyncExternalStore(
    subscribe,
    () => sesion,
    () => null,
  );
}

export function useBloqueada(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => bloqueada,
    () => false,
  );
}

export function usePuede(permiso: Permission): boolean {
  const s = useSesion();
  return s ? can(s.rol, permiso) : false;
}

/** Every permission from one hook call, for a screen that asks about a list of them. */
export function usePermisos(): (permiso: Permission) => boolean {
  const s = useSesion();
  return (permiso) => (s ? can(s.rol, permiso) : false);
}

export function sesionActual(): Sesion | null {
  return sesion;
}

export type ResultadoLogin =
  | { ok: true; sesion: Sesion }
  | {
      ok: false;
      motivo: 'usuario-desconocido' | 'inactivo' | 'pin-incorrecto' | 'espera';
      /** How long before the next try, in ms, after too many wrong ones (`intentos.ts`). */
      esperaMs?: number;
    };

/**
 * Check a PIN at the sign-in pad or on the lock screen, without letting anybody in yet: the pad
 * takes a beat to say so first, and `abrirSesion` is what opens the door.
 *
 * Every wrong one goes in the bitácora, under the account it was tried on, before the pad is
 * ready for the next try.
 */
export async function comprobarPin(usuarioId: string, pin: string): Promise<ResultadoLogin> {
  const usuario = await db.usuarios.get(usuarioId);
  if (!usuario) return { ok: false, motivo: 'usuario-desconocido' };
  if (!usuario.activo) return { ok: false, motivo: 'inactivo' };
  const quien = { usuarioId: usuario.id };
  const espera = esperaDe(quien);
  if (espera > 0) return { ok: false, motivo: 'espera', esperaMs: espera };

  if (!(await verifyPin(pin, usuario.pinHash))) {
    const donde = bloqueada ? 'desbloqueo' : 'entrada';
    await anotar('sesion.pin_incorrecto', { donde }, usuario.id);
    return { ok: false, motivo: 'pin-incorrecto', esperaMs: anotarFallo(quien) };
  }
  anotarAcierto(quien);
  return { ok: true, sesion: { usuarioId: usuario.id, nombre: usuario.nombre, rol: usuario.rol } };
}

/** Let in somebody whose PIN `comprobarPin` accepted, and put it in the bitácora. */
export async function abrirSesion(nueva: Sesion): Promise<void> {
  const donde = bloqueada ? 'desbloqueo' : 'entrada';
  await setMeta('usuarioActivoId', nueva.usuarioId);
  sesion = nueva;
  quitarBloqueo();
  marcarUso();
  notify();
  await anotar('sesion.iniciada', { donde }, nueva.usuarioId);
}

/** A wrong PIN on a PIN sheet, under whoever is signed in and typed it. */
export function anotarPinIncorrecto(donde: 'autorizacion' | 'confirmacion'): Promise<void> {
  return anotar('sesion.pin_incorrecto', { donde }, sesion?.usuarioId);
}

/**
 * Into the bitácora, without ever standing in the way. A sign-in that could not be written down
 * is still a sign-in, and a PIN pad that will not open because of the log would lock the shop
 * out of its own till.
 */
async function anotar<T extends 'sesion.iniciada' | 'sesion.pin_incorrecto'>(
  tipo: T,
  payload: PayloadInputOf<T>,
  actorId: string | undefined,
): Promise<void> {
  try {
    await emitir(tipo, payload, actorId ? { actorId } : {});
  } catch (e) {
    console.warn(`No se pudo anotar ${tipo} en la bitácora`, e);
  }
}

/**
 * Sign out, for good: forgetting who was signed in is what stops the next reload from putting
 * them straight back.
 */
export async function salir(): Promise<void> {
  await delMeta('usuarioActivoId');
  sesion = null;
  quitarBloqueo();
  notify();
}

/**
 * Whoever is signed in, as the users table now has them.
 *
 * The session is a copy of their row taken at sign-in, so without this an owner who made
 * somebody a cashier, or shut them out in «Personal y equipos», changed nothing for as long as
 * that person stayed signed in — a whole shift, on a till nobody locks. Deactivated, they are
 * signed out on the spot; a new role or name applies at once, their menu and permissions with it.
 */
export async function alCambiarUsuario(usuario: { id: string; nombre: string; rol: Role; activo: boolean }): Promise<void> {
  if (!sesion || sesion.usuarioId !== usuario.id) return;
  if (!usuario.activo) {
    await salir();
    return;
  }
  if (usuario.rol === sesion.rol && usuario.nombre === sesion.nombre) return;
  sesion = { usuarioId: usuario.id, nombre: usuario.nombre, rol: usuario.rol };
  notify();
}

/**
 * Restores the last signed-in user on reload.
 *
 * Whether they have to type their PIN first is not decided here: that is the lock's call, and
 * it asks only once the app has gone unused for a while (see `bloqueo.ts`). A shift involves
 * dozens of reloads and interruptions, and demanding a PIN after each one gets the PIN written
 * on a sticky note beside the till.
 */
export async function restaurarSesion(): Promise<Sesion | null> {
  const id = await getMeta('usuarioActivoId');
  if (!id) return null;
  const usuario = await db.usuarios.get(id);
  if (!usuario?.activo) return null;
  sesion = { usuarioId: usuario.id, nombre: usuario.nombre, rol: usuario.rol };
  notify();
  return sesion;
}

/** Put the PIN pad over the screen. The session and everything on screen stay as they are. */
export function bloquear(): void {
  if (!sesion || bloqueada) return;
  bloqueada = true;
  try {
    localStorage.setItem(CLAVE_BLOQUEADA, '1');
  } catch {
    // Without storage a reload forgets the lock, but it also forgets the last use (see
    // `ultimoUso`), and that asks for the PIN all the same.
  }
  notify();
}

/** Whether the app was closed or reloaded with the PIN pad up. */
export function estabaBloqueada(): boolean {
  try {
    return localStorage.getItem(CLAVE_BLOQUEADA) === '1';
  } catch {
    return false;
  }
}

function quitarBloqueo(): void {
  bloqueada = false;
  try {
    localStorage.removeItem(CLAVE_BLOQUEADA);
  } catch {
    // Nothing kept, nothing to remove.
  }
}

/**
 * The shop's idle minutes before the PIN pad comes back (`bloqueoMinutos`). Unreadable, it is
 * the default — a lock that cannot find its setting still locks.
 */
export const minutosSinUso = (): Promise<number> =>
  readSettings().then(
    (c) => c.bloqueoMinutos,
    () => MINUTOS_SIN_USO,
  );

/**
 * Watch for the app going unused (see `bloqueo.ts`), and lock it when it has.
 *
 * Checked every half minute, and at once when the app comes back into view, because a hidden
 * page's timers barely run. Touches on the PIN pad do not count as use: somebody trying PINs
 * has not been let in, and a reload must not let them in either.
 *
 * The minutes are read afresh at every check, because the owner can change them at any moment,
 * on this phone or on another one that syncs the change over.
 */
export function vigilarUso(): () => void {
  let escrito = 0;
  const tocar = () => {
    const ahora = Date.now();
    if (bloqueada || ahora - escrito < 5_000) return;
    escrito = ahora;
    marcarUso(ahora);
  };
  const revisar = async () => {
    const minutos = await minutosSinUso();
    if (hayQuePedirPin(ultimoUso(), Date.now(), minutos)) bloquear();
  };
  const alVolver = () => {
    if (document.visibilityState === 'visible') void revisar();
  };

  const opciones = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', tocar, opciones);
  window.addEventListener('keydown', tocar, opciones);
  document.addEventListener('visibilitychange', alVolver);
  const reloj = window.setInterval(() => void revisar(), 30_000);
  return () => {
    window.removeEventListener('pointerdown', tocar, opciones);
    window.removeEventListener('keydown', tocar, opciones);
    document.removeEventListener('visibilitychange', alVolver);
    window.clearInterval(reloj);
  };
}

/**
 * Owner PIN authorisation for an action the signed-in user cannot perform alone — going below
 * the margin floor, voiding someone else's sale. Verified locally so it works offline, and the
 * calling code is responsible for recording it in the bitácora.
 */
export async function autorizarDueno(pin: string): Promise<ResultadoPin> {
  const espera = esperaDe('dueno');
  if (espera > 0) return { ok: false, esperaMs: espera, sinProbar: true };
  /*
   * Every user, sorted out here. `activo` is a boolean, and IndexedDB does not index booleans: a
   * `where('activo').equals(1)` found nobody, without an error to fall back on, so no owner's PIN
   * was ever accepted — below the margin, past a fiado limit. The list is a handful of people.
   */
  const duenos = await db.usuarios.toArray();
  for (const u of duenos) {
    if (u.rol !== 'dueno' || !u.activo) continue;
    if (await verifyPin(pin, u.pinHash)) {
      anotarAcierto('dueno');
      return { ok: true, usuarioId: u.id };
    }
  }
  return { ok: false, esperaMs: anotarFallo('dueno') };
}

/** A PIN asked for on a sheet: whose it was when right, how long to wait after too many wrong. */
export interface ResultadoPin {
  ok: boolean;
  usuarioId?: string;
  esperaMs?: number;
  /** Turned away for a wait still running: the PIN was not tried at all. */
  sinProbar?: boolean;
}

/**
 * The signed-in user's own PIN, asked again before something done in their name — voiding a
 * sale.
 *
 * The PIN pad only comes back after the app has gone unused for a while (see `bloqueo.ts`), so
 * a phone handed across the counter inside that window is still signed in as whoever used it
 * last. Asking again at the moment it matters is what makes the name on a void the name of the
 * person who did it.
 */
export async function verificarPinPropio(pin: string): Promise<ResultadoPin> {
  if (!sesion) return { ok: false };
  const usuario = await db.usuarios.get(sesion.usuarioId);
  if (!usuario?.activo) return { ok: false };
  const quien = { usuarioId: usuario.id };
  const espera = esperaDe(quien);
  if (espera > 0) return { ok: false, esperaMs: espera, sinProbar: true };
  if (!(await verifyPin(pin, usuario.pinHash))) return { ok: false, esperaMs: anotarFallo(quien) };
  anotarAcierto(quien);
  return { ok: true, usuarioId: usuario.id };
}
